import { useCallback, useMemo, useEffect } from 'react'
import {
  withDefault,
  NumberParam,
  useQueryParams,
  StringParam,
  BooleanParam,
  createEnumParam
} from 'use-query-params'
import {
  CommaNumericArrayParam,
  SORTING_CHEAP,
  SORTING_EXPENSIVE,
  SORTING_NEW
} from '@plandi/common'
import debounce from 'lodash-es/debounce'

import { useQueryProjectCategories } from './project-queries'

const SortProjectsParam = createEnumParam([
  SORTING_NEW,
  SORTING_CHEAP,
  SORTING_EXPENSIVE
])

export const ProjectFiltersQueryParamConfigMap = {
  keyword: withDefault(StringParam, undefined, true),
  sort: withDefault(SortProjectsParam, undefined, true),
  categoryIds: withDefault(CommaNumericArrayParam, undefined, true),
  subCategoryIds: withDefault(CommaNumericArrayParam, undefined, true),
  fromCost: withDefault(NumberParam, undefined, true),
  toCost: withDefault(NumberParam, undefined, true),
  negotiated: withDefault(BooleanParam, undefined, true),
  fromBuildingArea: withDefault(NumberParam, undefined, true),
  toBuildingArea: withDefault(NumberParam, undefined, true),
  fromBuildingHeight: withDefault(NumberParam, undefined, true),
  toBuildingHeight: withDefault(NumberParam, undefined, true),
  bimModel: withDefault(BooleanParam, undefined, true),
  expertise: withDefault(BooleanParam, undefined, true),
  page: withDefault(NumberParam, 1, true),
  tag: withDefault(NumberParam, undefined, true)
}

export const ProjectsFiltersType = () => {
  const [projectsFilters, setParams] = useQueryParams(
    ProjectFiltersQueryParamConfigMap
  )
  const setProjectsFilters = useCallback(
    (value: Partial<typeof projectsFilters>) => {
      setParams((oldValue) => ({ ...oldValue, ...value }))
    },
    [setParams]
  )

  return setProjectsFilters
}

export const useProjectsFilters = () => {
  const [projectsFilters, setParams] = useQueryParams(
    ProjectFiltersQueryParamConfigMap
  )
  const setProjectsFilters = useCallback(
    (value: Partial<typeof projectsFilters>) => {
      setParams((oldValue) => ({ ...oldValue, ...value }))
    },
    [setParams]
  )

  const clearProjectFilters = () => {
    setParams((filter) =>
      Object.fromEntries(Object.keys(filter).map((key) => [key, undefined]))
    )
  }

  const setProjectsFiltersDebounce = useMemo(
    () =>
      debounce(
        (value: Parameters<typeof setProjectsFilters>[number]) =>
          setProjectsFilters(value),
        1000
      ),
    [setProjectsFilters]
  )

  useEffect(
    () => () => setProjectsFiltersDebounce.cancel(),
    [setProjectsFiltersDebounce]
  )

  return {
    ...projectsFilters,
    clearProjectFilters,
    setProjectsFilters,
    setProjectsFiltersDebounce
  }
}

export const useSelectedCategoriesByFilter = () => {
  const { categoryIds } = useProjectsFilters()
  const { data: selectedCategories } = useQueryProjectCategories({
    select: (categories) =>
      categories?.filter((category) =>
        categoryIds?.some((categoryId) => category.id === categoryId)
      )
  })

  return { selectedCategories }
}

type useSelectedSubCategoriesByFilterProps =
  | {
      categoryIds: number[] | undefined
    }
  | undefined

export const useSelectedSubCategoriesByFilter = (
  selectedCategoryIds: useSelectedSubCategoriesByFilterProps
) => {
  const { categoryIds, subCategoryIds } = useProjectsFilters()

  const { data: subCategories } = useQueryProjectCategories({
    select: (categories) =>
      categories
        ?.filter((category) =>
          !selectedCategoryIds
            ? categoryIds?.some((categoryId) => categoryId === category.id)
            : selectedCategoryIds.categoryIds?.some(
                (selectedCategoryId) => selectedCategoryId === category.id
              )
        )
        .flatMap((category) => category.subCategories)
  })

  return {
    subCategories,
    selectedSubCategories:
      subCategories?.filter((subCategory) =>
        subCategoryIds?.some(
          (subCategoryId) => subCategoryId === subCategory?.id
        )
      ) ?? []
  }
}
